import { useEffect, useState, useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore, selectIsAuthenticated, selectOnboarding } from '@/stores/useAuthStore'
import { Loader2 } from 'lucide-react'
import { api } from '@/api/client'

interface OnboardingStatus {
  step: number
  completed_at: string | null
}

interface ProtectedRouteProps {
  children: React.ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = useAuthStore(selectIsAuthenticated)
  const isLoading = useAuthStore((state) => state.isLoading)
  const onboarding = useAuthStore(selectOnboarding)
  const location = useLocation()
  const [onboardingChecked, setOnboardingChecked] = useState(false)
  const [redirectToOnboarding, setRedirectToOnboarding] = useState<string | null>(null)
  const hasCheckedRef = useRef(false)

  const isOnboardingPage = location.pathname === '/onboarding'
  // Maxflow Studio course buyers arrive via /login?redirect=%2Flearning, and a
  // fresh course-only account never has onboarding_completed_at set. Don't bounce
  // them into the SaaS onboarding wizard on the course surfaces — company
  // onboarding stays reachable later from within the app for buyers who go on to
  // want full CRM features.
  const isCourseRoute =
    location.pathname === '/learning' || location.pathname.startsWith('/learning/')
  // A merchant-initiated Shopify install lands here with ?shop=….
  // Bouncing them into the onboarding wizard drops that param on the floor and
  // the install they started on Shopify's side silently never happens.
  const isShopifyInstallLanding = location.pathname === '/settings/connections/shopify/install'
  // Backend OAuth callbacks land here with ?platform&status&session.
  // Picker flows hold their tokens only in a 10-minute Redis session — nothing
  // is persisted until the user picks an asset — so bouncing this landing into
  // the wizard destroys the connection the user just authorized.
  const isOAuthCallbackLanding = location.pathname === '/settings/connections/oauth-callback'
  // The MCP OAuth consent screen — here MaxFlow is the authorization
  // SERVER, not the client. FastMCP's /authorize redirects the browser to
  // /oauth/consent?txn=…, and that transaction is TTL-bounded and single-use:
  // bouncing into the wizard drops ?txn and destroys the authorization the
  // user's MCP client is waiting on, exactly as for the other OAuth landings above.
  const isMcpConsentLanding = location.pathname === '/oauth/consent'
  // GMB's web landing (and the picker forward from OAuthCallbackPage) is
  // /settings/accounts carrying an OAuth return signature: a picker `session`, or
  // GMB's `connected` / `gmb_error` outcome params. The pickers strip those
  // params from the URL as soon as they open (to avoid re-triggering on
  // refresh), so the skip must LATCH for as long as the user stays on the
  // landing pathname — re-evaluating the bare URL mid-picker would re-fire the
  // gate and destroy the connection. This instance wraps the whole app shell,
  // so the ref survives in-app navigation; any pathname change re-arms the gate.
  const searchParams = new URLSearchParams(location.search)
  const oauthLandingPathRef = useRef<string | null>(null)
  const isOAuthReturnOnAccounts =
    location.pathname === '/settings/accounts' &&
    (searchParams.has('session') || searchParams.has('connected') || searchParams.has('gmb_error'))
  if (isOAuthReturnOnAccounts) {
    oauthLandingPathRef.current = location.pathname
  } else if (oauthLandingPathRef.current !== location.pathname) {
    oauthLandingPathRef.current = null
  }
  // The plans page: onboarding's plan note links here, so an owner can upgrade before
  // finishing.
  const isPlansPage = location.pathname === '/settings/plan'
  const skipOnboardingGate =
    isOnboardingPage ||
    isPlansPage ||
    isCourseRoute ||
    isShopifyInstallLanding ||
    isOAuthCallbackLanding ||
    isMcpConsentLanding ||
    oauthLandingPathRef.current === location.pathname

  useEffect(() => {
    // On the onboarding page and the course surfaces, skip the API check entirely
    if (skipOnboardingGate) {
      setOnboardingChecked(true)
      setRedirectToOnboarding(null)
      return
    }

    if (!isAuthenticated || isLoading) {
      setOnboardingChecked(true)
      return
    }

    // If we already know onboarding is incomplete from the store
    // (e.g., just registered), redirect immediately without an API call
    if (onboarding && !onboarding.completed) {
      setRedirectToOnboarding('/onboarding')
      setOnboardingChecked(true)
      return
    }

    // Only make the API check once per mount
    if (hasCheckedRef.current) {
      setOnboardingChecked(true)
      return
    }

    let cancelled = false
    async function checkOnboarding() {
      try {
        const res = await api.get<OnboardingStatus>('/onboarding/business-status')
        const data = (res?.data ?? res) as unknown as OnboardingStatus
        if (!cancelled && !data.completed_at) {
          setRedirectToOnboarding(`/onboarding?step=${data.step}`)
        }
      } catch {
        // If the check fails, proceed normally (don't block the app)
      } finally {
        if (!cancelled) {
          hasCheckedRef.current = true
          setOnboardingChecked(true)
        }
      }
    }
    checkOnboarding()
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, isLoading, skipOnboardingGate, onboarding])

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader2 className="text-primary h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (!isAuthenticated) {
    // Round-trip the FULL location, not just the pathname. Dropping the query
    // string silently discarded the OAuth callback's ?platform&status params,
    // so a signed-in user landed on a bare /oauth-callback and was told
    // "Authorization failed" for a connection that had already succeeded.
    const from = `${location.pathname}${location.search}${location.hash}`
    return <Navigate to="/login" state={{ from }} replace />
  }

  if (!onboardingChecked) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader2 className="text-primary h-8 w-8 animate-spin" />
      </div>
    )
  }

  // Never redirect from the onboarding page itself or the course surfaces
  if (redirectToOnboarding && !skipOnboardingGate) {
    // Round-trip the FULL location, mirroring the unauthenticated branch above:
    // a bare pathname silently discarded OAuth-return query params for any
    // landing route not in the allow-list.
    const [basePath, baseSearch] = redirectToOnboarding.split('?')
    const merged = new URLSearchParams(baseSearch)
    searchParams.forEach((value, key) => {
      if (!merged.has(key)) merged.append(key, value)
    })
    const query = merged.toString()
    return <Navigate to={`${basePath}${query ? `?${query}` : ''}${location.hash}`} replace />
  }

  return <>{children}</>
}
